import React, { useState } from 'react';
import { motion } from 'framer-motion';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL?.trim();
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY?.trim();

export default function AccountDeletion() {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // TODO: Call the actual deletion request function here
    console.log('Deletion requested for:', email);
    const data = await fetch(`${supabaseUrl}/functions/v1/delete-account`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
        }),
      }).catch(e => console.log('Edge function trigger failed:', e));
      console.log('data',data)
    setIsSubmitted(true);
  };

  return (
    <div style={styles.pageContainer}>
      <motion.div 
        style={styles.contentWrapper}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header Section */}
        <motion.header style={styles.header} variants={itemVariants}>
          <div style={styles.logoMark}>OMNIO</div>
          <h1 style={styles.title}>Account Deletion</h1>
          <div style={styles.divider}></div>
          <p style={styles.subtitle}>REQUEST PERMANENT DELETION</p>
        </motion.header>

        {/* Intro */}
        <motion.p style={styles.intro} variants={itemVariants}>
          We value your privacy and your right to control your data. If you wish to delete your <span style={styles.highlight}>Omnio</span> account and all associated data, please submit your request below.
        </motion.p>

        {/* Form Section */}
        <motion.div variants={itemVariants} style={styles.formContainer}>
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.inputGroup}>
                <label htmlFor="email" style={styles.label}>Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your registered email"
                  required
                  style={styles.input}
                />
              </div>
              <button type="submit" style={styles.button}>
                Submit Request
              </button>
              <p style={styles.note}>
                * This action cannot be undone. All your data including workouts, meals, and preferences will be permanently removed.
              </p>
            </form>
          ) : (
            <div style={styles.successMessage}>
              <h3 style={styles.successTitle}>Request Received</h3>
              <p style={styles.successText}>
                We have received your request to delete the account associated with <span style={styles.highlight}>{email}</span>.
                We will process your request as soon as possible and you will receive a confirmation email when it is complete.
              </p>
            </div>
          )}
        </motion.div>

        {/* Footer */}
        <motion.footer style={styles.footer} variants={itemVariants}>
          <p>OMNIO</p>
          <p style={{ opacity: 0.5 }}>© 2026</p>
        </motion.footer>

      </motion.div>
    </div>
  );
}

const styles = {
  pageContainer: {
    backgroundColor: '#050505',
    color: '#F5F5F5',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    display: 'flex',
    justifyContent: 'center',
    padding: '80px 24px',
  },
  contentWrapper: {
    maxWidth: '700px',
    width: '100%',
  },
  header: {
    marginBottom: '60px',
  },
  logoMark: {
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '4px',
    color: '#444',
    marginBottom: '20px',
  },
  title: {
    fontSize: '3.5rem',
    fontWeight: '800',
    letterSpacing: '-1.5px',
    margin: '0 0 20px 0',
    background: 'linear-gradient(to bottom right, #fff, #888)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  divider: {
    height: '1px',
    width: '60px',
    backgroundColor: '#333',
    marginBottom: '20px',
  },
  subtitle: {
    fontSize: '11px',
    letterSpacing: '2px',
    color: '#666',
    textTransform: 'uppercase',
  },
  intro: {
    fontSize: '1.25rem',
    lineHeight: '1.6',
    color: '#999',
    marginBottom: '60px',
    fontWeight: '400',
  },
  highlight: {
    color: '#fff',
    fontWeight: '500',
  },
  formContainer: {
    marginBottom: '40px',
    padding: '40px',
    border: '1px solid #222',
    backgroundColor: 'rgba(255,255,255,0.02)',
    borderRadius: '4px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    color: '#888',
  },
  input: {
    padding: '16px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid #333',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  button: {
    padding: '16px',
    backgroundColor: '#fff',
    color: '#000',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    marginTop: '8px',
  },
  note: {
    fontSize: '12px',
    color: '#666',
    marginTop: '16px',
    lineHeight: '1.5',
  },
  successMessage: {
    textAlign: 'center',
    padding: '20px',
  },
  successTitle: {
    fontSize: '1.5rem',
    color: '#fff',
    marginBottom: '16px',
  },
  successText: {
    color: '#888',
    lineHeight: '1.6',
  },
  footer: {
    marginTop: '100px',
    borderTop: '1px solid #222',
    paddingTop: '30px',
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '10px',
    letterSpacing: '1px',
    color: '#444',
    textTransform: 'uppercase',
  }
};

